import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  selectPageIndex,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
  RHYTHM_EVENT_TAB_ID,
} from '@/entities/report';
import { EMITTER_CONSTANTS, KEY_RECOIL, STRIP_EVENT_THUMBNAIL_INFO } from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { checkAndProcessEcgData } from '@/entities/ecg';
import { bookmarkOffsetPreviewStrip, getStripIndex, getStripPosition } from '@/entities/strip';
import { calculateStartStopStripMomentOfListEventV2 } from '@/entities/event';
import type { StripBookmarksProps } from './stripBookmarks';

/**
 * Backs the paged grid of ECG bookmark preview strips. Slices the current page out of the store's bookmarks, prefetches the following page, keeps the selected strip and its viewer jump in step with paging and drag selection, and walks the selection with the arrow keys, emitting the next and previous page events at the grid edges.
 *
 * @param props - Grid inputs: the active button, study and profile ids, timezone offset, report start and the ECG data map.
 * @returns An object carrying selectionAreaWrapperRef, ecgBookmarksInPage, selectedStrip, isHasSelected, the thumbnail display settings, timezoneOffset, startReport, ecgDataMap and the setSelectedBookmark and handleClickSelectBookmark handlers.
 */
const useStripBookmarks = ({
  activeButton,
  studyId,
  profileId,
  timezoneOffset,
  startReport,
  ecgDataMap,
}: StripBookmarksProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Stores which page of bookmark strips the grid shows. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the new index.
   *
   * @param v - The new page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const sizeThumbnailConfig = useReportStore((s) => s.sizeEventThumbnailConfig);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Stores the bookmarks picked out by drag selection in the strip grid. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the new list.
   *
   * @param v - The new multi-selection list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the single bookmark strip that currently has focus in the grid. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the new strip.
   *
   * @param v - The newly selected strip, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  /**
   * Reads the full list of ECG bookmarks for the study. Takes it off the report store's current snapshot rather than subscribing, so the paging and caching logic sees the latest list without forcing a re-render.
   *
   * @returns The study's ECG bookmarks.
   */
  const getEcgBookmarks = () => useReportStore.getState().ecgBookmarks;
  // Subscribed only to re-page when the list arrives after the tab was opened (fetched after event counts).
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the ECG viewer jumps to when a bookmark strip is selected. Forwards the value to the store action with the tab-2 key already bound.
   *
   * @param v - The jump target date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets which portion of the day the ECG viewer lands on after a bookmark jump. Forwards the value to the store action with the tab-2 key already bound.
   *
   * @param v - The new jump portion, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const [ecgBookmarksInPage, setEcgBookmarksInPage] = useState<any[]>([]);

  const selectionAreaWrapperRef = useRef<any>();
  const stripPosition = useRef({ x: 0, y: 0 });
  const prevPageIndex = useRef(pageIndex.index);
  const arrowKeyToPrevPage = useRef(false);
  const timeoutRef = useRef<any>();
  const [selected, setSelected] = useState(new Set<string>());

  // Handle drag select
  /**
   * Replaces the drag-selection set behind the bookmark strip grid. Stores the incoming set as-is; an effect then resolves those keys into the selected bookmark objects.
   *
   * @param newSelected - The new set of selected strip keys.
   * @returns Nothing.
   */
  const setSelectedBookmark = (newSelected: any) => {
    setSelected(newSelected);
  };

  useEffect(() => {
    const selectedArr = [...selected];
    const selectedBookmarks: any[] = [];
    _.forEach(selectedArr, (item) => {
      const id = item.split('-')[1];
      /**
       * Resolves one drag-selected strip key back to the bookmark behind it. Matches the id parsed out of the key against the bookmarks on the current page, yielding nothing when that strip has since paged out.
       *
       * @returns The matching bookmark on the current page, or undefined.
       */
      const selectedBookmark = (ecgBookmarksInPage || []).find((bookmark) => bookmark.id === id);
      if (selectedBookmark) {
        selectedBookmarks.push(selectedBookmark);
      }
    });
    setSelectedMultipleStrip((prev: any) => {
      if (prev.length === 0 && selectedBookmarks.length === 0) {
        return prev;
      }
      return selectedBookmarks;
    });
  }, [selected, ecgBookmarksInPage]);

  /**
   * Selects a bookmark strip clicked in the grid. Does nothing when that strip is already selected, otherwise records its row and column position, stores it as the selected strip and asks the ECG viewer to jump to its centre at portion 2.
   *
   * @param strip - The clicked bookmark.
   * @param index - Its position within the current page's strip list.
   * @returns Nothing.
   */
  const handleClickSelectBookmark = useCallback(
    (strip: any, index: any) => {
      if (strip?.id !== selectedStrip?.id) {
        stripPosition.current = getStripPosition(index, STRIP_EVENT_THUMBNAIL_INFO.stripPerRow);
        setSelectedStrip(strip);
        setJumpDate(strip.center);
        setJumpPortion(2);
      }
    },
    [selectedStrip],
  );

  /**
   * Pre-fetches the ECG data for a page of bookmark strips before it is shown. Schedules a timer that deregisters itself from the tab's timeout store, slices that page out of the current bookmarks, and requests a five-second window per bookmark at the preview strip's offset.
   *
   * @param index - Zero-based page whose bookmarks should be cached.
   * @param waitTime - Delay in milliseconds before the prefetch runs.
   * @returns Nothing.
   */
  const handleCacheDataBackground = (index: number, waitTime = 300) => {
    const timerId = setTimeout(async () => {
      storeTimeout.removeStore(activeButton, timerId);
      const ecgBookmarks: any[] = getEcgBookmarks();
      const start = index * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit;
      const end = (index + 1) * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit;
      const ecgBookmarksInPage = ecgBookmarks.slice(start, end);
      if (ecgBookmarksInPage.length > 1) {
        const timeRanges = _.map(ecgBookmarksInPage, (item) => {
          const { defaultStartStrip, defaultStopStrip } =
            calculateStartStopStripMomentOfListEventV2({
              startTimeEpoch: item.startTimeEpoch,
            });
          return {
            start: defaultStartStrip.toISOString(),
            stop: defaultStopStrip.toISOString(),
          };
        });
      }
      _.forEach(ecgBookmarksInPage, (bookmark) => {
        checkAndProcessEcgData({
          ecgDataMap,
          beat: bookmark,
          dataLength: ecgDataMap.samplingFrequency * 5, // 5 seconds
          config: { customOffset: bookmarkOffsetPreviewStrip as any },
        });
      });
    }, waitTime);
    timeoutRef.current = timerId;
    storeTimeout.pushStore(activeButton, timerId);
  };

  /**
   * Rebuilds the current page of bookmark strips and re-anchors the selection. Slices the page out of the store's bookmarks, prefetches the next page when one exists, selects the last strip when the user arrowed backwards into the page and otherwise keeps the previously selected strip or falls back to the first, then jumps the viewer to whichever strip won.
   *
   * @returns A promise resolving once the page's strips and the selection are in state.
   */
  const handleGetBookmarks = async () => {
    const ecgBookmarks: any[] = getEcgBookmarks();
    const totalStrip = ecgBookmarks.length;
    const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;
    const start = validatePage * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit;
    const end = (validatePage + 1) * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit;
    const ecgBookmarksInPage = ecgBookmarks.slice(start, end);
    if (totalStrip > (validatePage + 1) * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit) {
      handleCacheDataBackground(validatePage + 1);
    }
    if (ecgBookmarksInPage.length > 1) {
      const timeRanges = _.reduce(
        ecgBookmarksInPage,
        (result, item, index) => {
          if (index > 0) {
            const { defaultStartStrip, defaultStopStrip } =
              calculateStartStopStripMomentOfListEventV2({
                startTimeEpoch: item.startTimeEpoch,
              });
            result.push({
              start: defaultStartStrip.toISOString(),
              stop: defaultStopStrip.toISOString(),
            });
          }
          return result;
        },
        [],
      );
    }
    if (pageIndex.index < prevPageIndex.current && arrowKeyToPrevPage.current) {
      stripPosition.current = getStripPosition(
        ecgBookmarksInPage.length - 1,
        STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
      );
      const strip = ecgBookmarksInPage[ecgBookmarksInPage.length - 1] || null;
      setSelectedStrip(strip);
      if (strip?.center) {
        setJumpDate(strip.center);
        setJumpPortion(2);
      }
    } else {
      const selectedStripIndex = _.findIndex(ecgBookmarksInPage, (x) => x.id === selectedStrip?.id);
      const newSelectedStrip =
        selectedStripIndex === -1
          ? ecgBookmarksInPage[0] || null
          : ecgBookmarksInPage[selectedStripIndex];
      stripPosition.current = getStripPosition(
        selectedStripIndex === -1 ? 0 : selectedStripIndex,
        STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
      );
      setSelectedStrip((prev: any) => {
        if (newSelectedStrip?.id !== prev?.id) {
          return newSelectedStrip;
        }
        return prev;
      });
      if (newSelectedStrip?.center) {
        setJumpDate(newSelectedStrip.center);
        setJumpPortion(2);
      }
    }
    arrowKeyToPrevPage.current = false;
    prevPageIndex.current = pageIndex.index;
    setEcgBookmarksInPage(ecgBookmarksInPage);
  };

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton, timeoutRef.current);
    },
    [activeButton],
  );

  useEffect(() => {
    handleGetBookmarks();
  }, [activeButton, pageIndex, ecgBookmarks]);

  useEffect(() => {
    /**
     * Moves the bookmark strip selection with the arrow keys while the rhythm-event tab is hovered. Resets the drag selection, walks the grid position by row or column, selects the strip at the new index and jumps the viewer to its centre, or emits the next and previous page events when the move runs past an edge.
     *
     * @param event - The document keydown event.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (_.isEmpty(selectionAreaWrapperRef.current) || event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(RHYTHM_EVENT_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      event.preventDefault();
      const { key } = event;
      switch (key) {
        case 'ArrowRight': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y += 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index > ecgBookmarksInPage.length - 1) {
            stripPosition.current.y -= 1;
            emitter.emit(EMITTER_CONSTANTS.RHYTHM_EVENTS_NEXT);
          } else {
            const strip = ecgBookmarksInPage[index];
            if (strip) {
              setSelectedStrip(strip);
              setJumpDate(strip.center);
              setJumpPortion(2);
            } else {
              stripPosition.current.y -= 1;
            }
          }
          break;
        }
        case 'ArrowLeft': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y -= 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index < 0) {
            arrowKeyToPrevPage.current = true;
            stripPosition.current.y += 1;
            emitter.emit(EMITTER_CONSTANTS.RHYTHM_EVENTS_PREV);
          } else {
            const strip = ecgBookmarksInPage[index];
            if (strip) {
              setSelectedStrip(strip);
              setJumpDate(strip.center);
              setJumpPortion(2);
            } else {
              stripPosition.current.y += 1;
            }
          }
          break;
        }
        case 'ArrowUp': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x -= 1;
          const strip =
            ecgBookmarksInPage[
              getStripIndex(stripPosition.current, STRIP_EVENT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (strip) {
            setSelectedStrip(strip);
            setJumpDate(strip.center);
            setJumpPortion(2);
          } else {
            stripPosition.current.x += 1;
          }
          break;
        }
        case 'ArrowDown': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x += 1;
          const strip =
            ecgBookmarksInPage[
              getStripIndex(stripPosition.current, STRIP_EVENT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (strip) {
            setSelectedStrip(strip);
            setJumpDate(strip.center);
            setJumpPortion(2);
          } else {
            stripPosition.current.x -= 1;
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [ecgBookmarksInPage]);

  useUpdateEffect(() => {
    const { x: row, y: column } = stripPosition.current;
    const currentStripIndexInList = column + row * STRIP_EVENT_THUMBNAIL_INFO.prevStripPerRow;
    const currentEventIndex =
      currentStripIndexInList + pageIndex.index * STRIP_EVENT_THUMBNAIL_INFO.prevStripDisplayLimit;
    const newPage = Math.floor(currentEventIndex / STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit);
    setPageIndex({ index: newPage < 0 ? 0 : newPage });
  }, [sizeThumbnailConfig]);
  const isHasSelected = useMemo(
    () => !_.isEmpty(selectedMultipleStrip) || !_.isEmpty(selectedStrip),
    [selectedMultipleStrip, selectedStrip],
  );

  return {
    selectionAreaWrapperRef,
    ecgBookmarksInPage,
    selectedStrip,
    isHasSelected,
    highPassThumbnail,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
    timezoneOffset,
    startReport,
    ecgDataMap,
    setSelectedBookmark,
    handleClickSelectBookmark,
  };
};

export default useStripBookmarks;
